import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { useVariables } from 'pared/Routes/renderer/variables'
import { getDateRangeLabelV2 } from 'pared/utils/date'

import { useDateFilter } from '../../../dateFilter'
import { IApiDataType } from '../../types'

interface IDataType {
  trendLocationGroupPurchase: {
    nodes: {
      startDate: string
      endDate: string
      businessYear: string
      businessQuarter: string
      businessMonth: string
      businessWeekOfMonth: string
      businessWeek: string
      netSales: number
      itemDetails: Record<
        string,
        {
          type: 'all' | 'item' | 'category'
          item: string
          actualUsage: number
          actualUsagePercent: number
          end: number
          percent: number
        }
      >
    }[]
  }
}

const query = gql`
  query trendLocationGroupPurchase(
    $iLocationGroupId: Int!
    $iEndDate: Date!
    $iGroupBy: String!
    $iDateRangeNumber: Int!
  ) {
    trendLocationGroupPurchase(
      iLocationGroupId: $iLocationGroupId
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iDateRangeNumber: $iDateRangeNumber
    ) {
      nodes {
        startDate
        endDate
        businessYear
        businessQuarter
        businessMonth
        businessWeekOfMonth
        businessWeek
        netSales
        itemDetails
      }
    }
  }
`

export const bbbTrendLocationGroupPurchaseConfigs = {
  date: 'string',
  netSales: 'price',
  actualUsage: 'price',
  actualUsagePercent: 'percent',
  endInventory: 'price',
  percentOfNetSales: 'percent',
  percentOfNetSalesHeatMap: 'percent',
} as const

const useBbbTrendLocationGroupPurchase = () => {
  const { variables } = useVariables()
  const { startDate, endDate } = useDateFilter()
  const locationGroupIds = variables.allStores?.locationGroupIds

  const { data, loading } = useQuery<IDataType>(query, {
    variables: {
      iLocationGroupId: locationGroupIds?.[0],
      iEndDate: endDate,
      iGroupBy: 'last_x_weeks',
      iDateRangeNumber: 8,
    },
    skip: !startDate || !endDate || !locationGroupIds,
  })
  const category =
    variables.bbbPurchaseCategories?.value[0]?.slice(-1)[0] || 'All Items'
  const categoryIsLoading = variables.bbbPurchaseCategories?.loading

  return {
    data: useMemo((): IApiDataType => {
      const trendData = data?.trendLocationGroupPurchase.nodes

      if (!trendData || !category) return null

      return trendData.map((d) => {
        const value = d.itemDetails?.[category]

        return {
          ...value,
          date:
            'startDate' in d
              ? getDateRangeLabelV2('business_week', {
                  ...d,
                  groupByStartDate: d.startDate,
                  groupByEndDate: d.endDate,
                })
              : null,
          netSales: d.netSales,
          endInventory: value?.end,
          percentOfNetSales: value?.percent,
        }
      })
    }, [data, category]),
    loading: loading || categoryIsLoading,
  }
}

export default useBbbTrendLocationGroupPurchase
